import { authentikConfig } from './authentik.config';

export const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:10555';
export const API_BASE_URL = BACKEND_BASE_URL + '/api';
// Base URL for uploaded files (logos, images). May differ from BACKEND_BASE_URL
// when the backend is proxied under a subpath (e.g. /api on the test server).
export const UPLOADS_BASE_URL = process.env.REACT_APP_UPLOADS_URL || BACKEND_BASE_URL;

let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

const onRefreshed = (token: string) => {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

export const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = localStorage.getItem('refresh_token');

  if (!refreshToken) {
    return null;
  }

  try {
    const response = await fetch(authentikConfig.tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: authentikConfig.clientId,
        refresh_token: refreshToken,
      }),
    });

    if (!response.ok) {
      // Refresh token is invalid, clear everything
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      return null;
    }

    const tokens = await response.json();
    localStorage.setItem('access_token', tokens.access_token);

    if (tokens.refresh_token) {
      localStorage.setItem('refresh_token', tokens.refresh_token);
    }

    return tokens.access_token;
  } catch (error) {
    console.error('Token refresh failed:', error);
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    return null;
  }
};

// Generic authenticated fetch with automatic token refresh
export const authenticatedFetch = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  const makeRequest = async (token: string | null): Promise<Response> => {
    const config: RequestInit = {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': token ? `Bearer ${token}` : '',
      },
    };

    return fetch(url, config);
  };

  let token = localStorage.getItem('access_token');
  let response = await makeRequest(token);

  // If we get a 401, try to refresh the token
  if (response.status === 401) {
    if (!isRefreshing) {
      isRefreshing = true;

      const newToken = await refreshAccessToken();

      if (newToken) {
        onRefreshed(newToken);
        isRefreshing = false;

        // Retry the original request with new token
        return makeRequest(newToken);
      } else {
        isRefreshing = false;
        // Refresh failed, redirect to login
        const base = (process.env.PUBLIC_URL || '').replace(/\/+$/, '');
        window.location.href = `${base}/login`;
        return response;
      }
    } else {
      // Wait for the ongoing refresh to complete
      return new Promise((resolve) => {
        addRefreshSubscriber((newToken: string) => {
          resolve(makeRequest(newToken));
        });
      });
    }
  }

  return response;
};

// Backend API request wrapper (uses authenticatedFetch)
export const apiRequest = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> => {
  const defaultHeaders: HeadersInit = {};

  // If body is not FormData, set Content-Type to application/json
  if (options.body && !(options.body instanceof FormData)) {
    defaultHeaders['Content-Type'] = 'application/json';
  }

  const config: RequestInit = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  return authenticatedFetch(`${API_BASE_URL}${endpoint}`, config);
};
